import React from 'react'
import { Table, Caption, Thead, Tr, Th, Tbody } from '@patternfly/react-table'
import ReviewRow from './ReviewRow'
import { hasAllRequired } from '../helpers/labels'

export function ServicesTable(props: {
  sortedServiceResults: any[],
  businessData: any,
  repoNameMap: Record<string, string>,
  repoFullMap: Record<string, string>,
  branchMapping: Record<string, string>,
  applicationBranch: string,
  deployTagMapping: Record<string, string>,
  setBranchMapping: React.Dispatch<React.SetStateAction<Record<string, string>>>,
  setDeployTagMapping: React.Dispatch<React.SetStateAction<Record<string, string>>>,
  imageValidationMap: Record<string, 'found' | 'not found'>,
  setImageValidationMap: React.Dispatch<React.SetStateAction<Record<string, 'found' | 'not found'>>>,
  refreshingApp: string | null,
  refreshBulkResultForApp: (app: string, newBranch: string) => Promise<void>,
  checkboxStates: Record<string, boolean>,
  showLatestImages: boolean,
  versionsByApp: Record<string, Record<string, string>>,
  labelsByApp: Record<string, { found: { name: string; value: string }[]; domainValue: string | null }>,
  storedLabelsInfo: Record<string, { requiredLabels: Record<string, boolean>, found: {name:string; value:string}[] }>
}) {
  const {
    sortedServiceResults,
    businessData,
    repoNameMap,
    repoFullMap,
    branchMapping,
    applicationBranch,
    deployTagMapping,
    setBranchMapping,
    setDeployTagMapping,
    imageValidationMap,
    setImageValidationMap,
    refreshingApp,
    refreshBulkResultForApp,
    checkboxStates,
    showLatestImages,
    versionsByApp,
    labelsByApp,
    storedLabelsInfo
  } = props

  return (
    <Table aria-label="Services to Deploy" gridBreakPoint="grid-lg">
      <Caption>Please review your selected services</Caption>
      <Thead>
        <Tr>
          <Th>Application</Th>
          <Th>Feature Branch</Th>
          <Th>Branch Status</Th>
          <Th>Update Branch</Th>
          <Th>Required Labels</Th>
          <Th>Image Validation</Th>
          {showLatestImages && <Th>Latest Built Images</Th>}
          <Th>Latest Commits</Th>
        </Tr>
      </Thead>
      <Tbody>
        {sortedServiceResults.map((res: any) => {
          const meta = businessData.releases.find((r: any) => r.name === res.application)
          const defaultBranch = meta?.production_branch || meta?.main_branches?.[0] || 'master'
          let branchValidation: { behind_by: number } | null = null
          if (res.behindInfo && typeof res.behindInfo === 'object') {
            branchValidation = { behind_by: res.behindInfo.behind_by }
          }

          const deployTags = res.images?.deploy_tags || []
          const versionsMap = versionsByApp[res.application] || {}

          const selectedTag = deployTagMapping[res.application]
            ? `commit-${deployTagMapping[res.application]}`
            : deployTags[0]

          const foundTags = res.images?.found_tags || []
          const latestImages = res.images?.latest_images || []
          const tsOnly = latestImages.map((tag: string) => {
            const version = versionsMap[tag]
            return version ? `${tag} (v${version})` : tag
          })

          const requiredOk = hasAllRequired(res.application, storedLabelsInfo, labelsByApp)

          return (
            <ReviewRow
              key={res.application}
              application={res.application}
              repoNameMap={repoNameMap}
              branchMapping={branchMapping}
              applicationBranch={applicationBranch}
              deployTag={selectedTag || 'N/A'}
              setBranchMapping={setBranchMapping}
              setDeployTagMapping={setDeployTagMapping}
              imageValidation={imageValidationMap[res.application] ?? (selectedTag && !foundTags.includes(selectedTag) ? 'not found' : 'found')}
              setImageValidationMap={setImageValidationMap}
              branchValidation={branchValidation}
              dummyImages={deployTags.map((tag: string) => {
                const short = tag.replace(/^commit-/, '')
                const version = versionsMap[tag]
                return version ? `commit-${short} (${version})` : `commit-${short}`
              })}
              latestImages={foundTags.length === 0 ? tsOnly : undefined}
              isTopLevel={!!checkboxStates[res.application]}
              exists={res.exists}
              actualBranchUsed={res.branch}
              refreshing={refreshingApp === res.application}
              refreshBulkResultForApp={refreshBulkResultForApp}
              repository={repoFullMap[res.application]}
              defaultBranch={defaultBranch}
              requiredLabelsOk={requiredOk}
            />
          )
        })}
      </Tbody>
    </Table>
  )
}


